import React, {FC, useState} from "react";
import {DbAttachment} from "../../db/DbSchema";
import {uuid} from "../../util/Uuid";
import {AttachmentDto} from "../../domain/AttachmentDto";
import {AttachmentList} from "../email/AttachmentList";
import ConfirmDialog from "../common/ConfirmDialog";
import {isImageFile} from "../../util/FileUtil";
import {AttachmentActions} from "../../actions/AttachmentActions";
import {createImageThumbnail} from "../../util/ImageUtil";
import {FileUploadControl, FileUploadControlRef} from "../common/FileUploadControl";
import {DropDownMenuItem} from "../toolbar/DropDownMenu";
import GetApp from "@mui/icons-material/GetApp";
import {useAppDispatch, useAppSelector, useAppStore} from "../../hooks/ReduxHooks";
import CreateIcon from "@mui/icons-material/Create";
import {TextFieldDialog} from "../common/TextFieldDialog";
import {getAttachmentImageSource} from "../../util/MimeUtil";
import {SystemActions} from "../../actions/SystemActions";

interface AttachmentManagerViewProps {
    emailUid: string;
    fileControlRef: FileUploadControlRef;
}

export async function convertFileToAttachment(emailUid: string, file: File): Promise<DbAttachment> {
    const attachment: DbAttachment = {
        emailUid,
        attachmentUid: uuid(),
        name: file.name,
        size: file.size,
        contentId: null, // TODO: support adding attachments with content ids
        contentType: file.type,
        hasData: true,
    };
    if (isImageFile(file.name)) {
        attachment.thumbnail = await createImageThumbnail(file);
    }
    return attachment;
}

export const AttachmentManagerView: FC<AttachmentManagerViewProps> = ({emailUid, fileControlRef}) => {

    const dispatch = useAppDispatch();

    const draftAttachments = useAppSelector(s => s.compose.draftAttachments);
    const contextAttachmentIsPendingUpload = useAppSelector(s => !!s.itemState.contextAttachment?.IsPendingUpload);

    const [isRemovingAttachment, setIsRemovingAttachment] = useState(false);
    const [attachmentToRemove, setAttachmentToRemove] = useState<AttachmentDto | null>(null);
    const [showRenameDialog, setShowRenameDialog] = useState(false);

    const store = useAppStore();

    async function attachmentFilesSelected(files: FileList) {
        if (!draftAttachments) {
            return;
        }

        for (let i = 0; i < files.length; i++) {
            const file: File = files[i];

            if (file.size > 40 * 1024 * 1024) {
                dispatch(SystemActions.showError(`Unable to attach ${file.name} as it's larger than 40MB`));
                continue;
            }

            const newAttachment = await convertFileToAttachment(emailUid, file);

            dispatch(await AttachmentActions.addAttachment(emailUid, file, newAttachment));
        }
    }

    async function removeAttachment() {
        if (attachmentToRemove && draftAttachments) {
            dispatch(await AttachmentActions.removeAttachment(emailUid, attachmentToRemove.Uid));
        }
    }

    async function renameAttachment(newName: string | null) {
        setShowRenameDialog(false);
        const attachment = draftAttachments?.find(a => a.attachmentUid === attachmentToRemove?.Uid);
        if (attachment && newName) {
            attachment.name = newName;
            dispatch(await AttachmentActions.renameAttachment(attachment));
        }
    }

    const attachmentDtos: AttachmentDto[] | undefined = draftAttachments ? draftAttachments
        .map(a => ({
            Id: undefined,
            Name: a.name,
            ContentType: a.thumbnail ? "Thumbnail" : "",
            Image: a.thumbnail || getAttachmentImageSource(a.contentType),
            Uid: a.attachmentUid,
            ContentId: a.contentId,
            DataSize: a.size,
            IsInline: false,
            IsPendingUpload: !a.hasData,
        })) : undefined;

    const contextMenuItems: (DropDownMenuItem | string)[] = [
        {
            icon: <CreateIcon/>,
            name: "Rename Attachment",
            disabled: contextAttachmentIsPendingUpload,
            onClick: () => {
                const contextAttachment = store.getState().itemState.contextAttachment;
                if (contextAttachment) {
                    setAttachmentToRemove(contextAttachment);
                    setShowRenameDialog(true);
                }
            },
        },
        "sep1",
        {
            icon: <GetApp/>,
            name: "Remove Attachment",
            onClick: () => {
                setAttachmentToRemove(store.getState().itemState.contextAttachment || null);
                setIsRemovingAttachment(true);
            },
        }];

    return (
        <>
            <FileUploadControl multiple fileControlRef={fileControlRef} onFilesSelected={attachmentFilesSelected}/>

            {
                !!draftAttachments?.length && !!attachmentDtos
                && (
                    <AttachmentList
                        attachments={attachmentDtos}
                        emailId={-1}
                        contextMenuItems={contextMenuItems}
                        onSelectAttachment={(attachment) => {
                            setAttachmentToRemove(attachment);
                            setIsRemovingAttachment(true);
                        }}/>
                )
            }

            <ConfirmDialog message={`Are you sure you want to remove the attachment ${attachmentToRemove?.Name}?`}
                           title="Remove attachment"
                           open={isRemovingAttachment}
                           onConfirm={async confirmed => {
                               if (confirmed) {
                                   await removeAttachment();
                               }
                               setIsRemovingAttachment(false);
                           }}
                           confirmColour="error"/>

            <TextFieldDialog title="Rename Attachment"
                             message="Enter the new attachment name"
                             initialValue={attachmentToRemove?.Name ?? ""}
                             open={showRenameDialog}
                             onClose={newName => renameAttachment(newName)}/>
        </>
    );
};
